import { Button, Modal } from '@components/common';
import { FormSelect } from '@components/common/FormField';
import {
  AccountRecord,
  PaymentMethodAccountMappingRecord,
  PaymentMethodRecord,
} from '@/renderer/types/accounting';
import React, { useMemo, useState } from 'react';

interface PaymentMethodMappingFormModalProps {
  accounts: AccountRecord[];
  isSaving: boolean;
  mapping: PaymentMethodAccountMappingRecord | null;
  paymentMethod: PaymentMethodRecord;
  onClose: () => void;
  onSubmit: (accountId: string) => void;
}

export function PaymentMethodMappingFormModal({
  accounts,
  isSaving,
  mapping,
  paymentMethod,
  onClose,
  onSubmit,
}: PaymentMethodMappingFormModalProps) {
  const [accountId, setAccountId] = useState(mapping?.account_id || '');
  const [error, setError] = useState<string | null>(null);

  const eligibleAccounts = useMemo(
    () => accounts.filter((account) => account.is_active),
    [accounts],
  );

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (!accountId) {
      setError('Select an account to save this mapping.');
      return;
    }

    setError(null);
    onSubmit(accountId);
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`${mapping ? 'Edit' : 'Create'} payment mapping`}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3">
          <p className="text-sm font-semibold text-gray-900">
            {paymentMethod.name}
          </p>
          <p className="mt-1 text-sm text-gray-500">
            Choose the account that should receive postings for this payment
            method.
          </p>
        </div>

        <FormSelect
          label="Account"
          required
          value={accountId}
          onChange={(event) => setAccountId(event.target.value)}
        >
          <option value="">Select account</option>
          {eligibleAccounts.map((account) => (
            <option key={account.id} value={account.id}>
              {account.code} - {account.name}
            </option>
          ))}
        </FormSelect>

        {error ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-4">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isSaving}>
            Save mapping
          </Button>
        </div>
      </form>
    </Modal>
  );
}
